import type { ICampaignCategory } from '../../../components/EsForm/CampaignCategorySelector';
import type { IAwardCategory } from '../../../redux/contest/types';
import type { SubmissionCategory } from '../../../types/global/helper';
import type { ContestWizardState } from '../contestWizard/types';
import { reconcilePrizeStructureWithTiers } from '../contestWizard/awardPresets';
import { awardPrizeCentsToFormDollars, getAwardPrizeCents } from '../contestWizard/buildAwardCategoryPayload';
import { plainTextFromHtml } from '../contestWizard/richTextUtils';

export function emptyWizardCategory(): ICampaignCategory {
    return {
        category_name: '',
        category_description: '',
        category_awards: [],
        with_fee: true,
        with_tiered_pricing: true,
        currency: 'USD',
        tier_fee: [
            { label: 'Standard', fee: 49, currency: 'USD' },
            { label: 'Premium', fee: 99, currency: 'USD' },
        ],
        prize_structure: [],
    };
}

function tierFeeFromSubmission(tier_fee: SubmissionCategory['tier_fee']) {
    if (!tier_fee?.length) {
        return emptyWizardCategory().tier_fee;
    }
    return tier_fee.map(t => ({
        label: t.label,
        fee: t.bare_amount != null ? t.bare_amount / 100 : parseFloat(String(t.fee)) || 0,
        currency: t.currency || 'USD',
        starts_at: t.starts_at ?? null,
        ends_at: t.ends_at ?? null,
        timezone: t.timezone ?? null,
        is_active: t.is_active,
        _id: t._id,
    }));
}

const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

export function isConfiguredAward(award: IAwardCategory): boolean {
    return getAwardPrizeCents(award.prize) > 0 || (award.additional_prize || []).some(Boolean);
}

function awardPrizeToWizardDollars(prize: number | string | null | undefined): number {
    return parseFloat(awardPrizeCentsToFormDollars(prize)) || 0;
}

function awardIdsFromPrizeStructure(category: SubmissionCategory): string[] {
    const ids: string[] = [];
    for (const placement of category.prize_structure || []) {
        for (const payout of placement.payouts || []) {
            if (typeof payout === 'string' && payout.trim()) ids.push(payout);
            else if (typeof payout === 'object' && payout?._id) ids.push(payout._id);
        }
    }
    return ids;
}

/** Awards linked to a submission category via prize_structure payout ids or tier_fee ids. */
export function awardsForSubmissionCategory(
    category: SubmissionCategory,
    awardCategories: IAwardCategory[] | undefined,
    awardById: Map<string, IAwardCategory>,
): IAwardCategory[] {
    const linked = new Map<string, IAwardCategory>();
    for (const id of awardIdsFromPrizeStructure(category)) {
        const award = awardById.get(id);
        if (award) linked.set(award._id, award);
    }
    const tierIds = new Set((category.tier_fee || []).map(t => t._id).filter(Boolean) as string[]);
    for (const award of awardCategories || []) {
        if (award.tier_name && tierIds.has(award.tier_name)) {
            linked.set(award._id, award);
        }
    }
    return Array.from(linked.values());
}

export type AwardCategoryEditShape = IAwardCategory & {
    category: string;
    position?: string;
    tier_label?: string;
};

/** Shape expected by CreateAwardCategoryDialog when editing a linked award. */
export function resolveAwardPlacementForDelete(
    award: IAwardCategory,
    submissionCategory: SubmissionCategory,
): string | undefined {
    if (submissionCategory.with_tiered_pricing && submissionCategory.prize_structure?.length) {
        for (const prize of submissionCategory.prize_structure) {
            if (
                Array.isArray(prize.payouts) &&
                prize.payouts.some(
                    payout => (typeof payout === 'string' ? payout : (payout as IAwardCategory)._id) === award._id,
                )
            ) {
                return prize.position;
            }
        }
    }
    return award.tier_position?.trim() || award.category_title?.trim() || undefined;
}

export function prepareAwardCategoryForEdit(
    award: IAwardCategory,
    submissionCategory: SubmissionCategory,
): AwardCategoryEditShape {
    const tier = (submissionCategory.tier_fee || []).find(
        t => t._id === award.tier_name || t.label === award.tier_name,
    );
    const tierLabel =
        tier?.label?.trim() ||
        (award.tier_name && !MONGO_OBJECT_ID_RE.test(award.tier_name) ? award.tier_name.trim() : '');
    const position = resolveAwardPlacementForDelete(award, submissionCategory) || 'Prize';

    return {
        ...award,
        category: submissionCategory._id!,
        position,
        tier_position: award.tier_position || position,
        tier_label: tierLabel,
    };
}

export function tierLabelForAward(category: SubmissionCategory, award: IAwardCategory): string {
    const tier = (category.tier_fee || []).find(t => t._id && t._id === award.tier_name);
    if (tier?.label?.trim()) return tier.label.trim();
    const rawTier = (award.tier_name || '').trim();
    if (rawTier && !(category.tier_fee || []).some(t => t._id === rawTier)) return rawTier;
    return award.tier_position?.trim() || award.category_title?.trim() || 'Prize';
}

function structureHasConfiguredPayouts(structure: NonNullable<ICampaignCategory['prize_structure']>): boolean {
    return structure.some(pl =>
        (pl.payouts || []).some(
            po => Number(po.prize) > 0 || (po.additional_prize || []).some(extra => Boolean(extra?.trim())),
        ),
    );
}

function buildPrizeStructureFromLinkedAwards(
    cat: SubmissionCategory,
    linkedAwards: IAwardCategory[],
): NonNullable<ICampaignCategory['prize_structure']> {
    const tiers = cat.tier_fee || [];
    const tierLabels = tiers.map(t => t.label?.trim()).filter(Boolean) as string[];
    const labels = tierLabels.length ? tierLabels : ['Standard'];

    const byPlacement = new Map<string, IAwardCategory[]>();
    for (const award of linkedAwards) {
        const placement = award.tier_position?.trim() || award.category_title?.trim() || 'Prize';
        const group = byPlacement.get(placement) ?? [];
        group.push(award);
        byPlacement.set(placement, group);
    }

    return Array.from(byPlacement.entries()).map(([position, awards]) => ({
        position,
        payouts: labels.map((label, tierIndex) => {
            const tierId = tiers[tierIndex]?._id;
            const award =
                (tierId ? awards.find(a => a.tier_name === tierId) : undefined) ??
                awards.find(a => a.tier_name?.trim() === label) ??
                (labels.length === 1 && awards.length === 1 ? awards[0] : undefined);
            if (!award) {
                return { label, prize: 0, additional_prize: [] as string[] };
            }
            return {
                _id: award._id,
                label,
                prize: awardPrizeToWizardDollars(award.prize),
                additional_prize: [...(award.additional_prize || [])],
            };
        }),
    }));
}

function prizeStructureFromSubmission(
    cat: SubmissionCategory,
    awardCategories: IAwardCategory[] | undefined,
    awardById: Map<string, IAwardCategory>,
): ICampaignCategory['prize_structure'] {
    const tierLabels = (cat.tier_fee || []).map(t => t.label?.trim()).filter(Boolean) as string[];
    const fromStructure = (cat.prize_structure || [])
        .map(pl => ({
            position: pl.position?.trim() || '',
            payouts: (pl.payouts || []).map((p, payoutIndex) => {
                const tierLabel = tierLabels[payoutIndex] || tierLabels[0] || 'Standard';
                if (typeof p === 'string') {
                    const award = awardById.get(p);
                    return {
                        ...(award?._id ? { _id: award._id } : { _id: p }),
                        label: tierLabel,
                        prize: award ? awardPrizeToWizardDollars(award.prize) : 0,
                        additional_prize: award?.additional_prize ?? [],
                    };
                }
                if (typeof p === 'object' && p && '_id' in p) {
                    const award = p as IAwardCategory;
                    const rawLabel = (award.tier_name || award.category_title || '').trim();
                    const label =
                        !rawLabel || MONGO_OBJECT_ID_RE.test(rawLabel) || !tierLabels.includes(rawLabel)
                            ? tierLabel
                            : rawLabel;
                    return {
                        _id: award._id,
                        label,
                        prize: awardPrizeToWizardDollars(award.prize),
                        additional_prize: award.additional_prize ?? [],
                    };
                }
                const payout = p as { _id?: string; label?: string; prize?: number; additional_prize?: string[] };
                const rawLabel = (payout.label || '').trim();
                const label = !rawLabel || MONGO_OBJECT_ID_RE.test(rawLabel) ? tierLabel : rawLabel;
                return {
                    ...(payout._id ? { _id: payout._id } : {}),
                    label,
                    prize: Number(payout.prize) || 0,
                    additional_prize: payout.additional_prize ?? [],
                };
            }),
        }))
        .filter(pl => pl.position || structureHasConfiguredPayouts([pl]));

    if (structureHasConfiguredPayouts(fromStructure)) {
        return fromStructure;
    }

    const linked = awardsForSubmissionCategory(cat, awardCategories, awardById).filter(isConfiguredAward);
    if (linked.length) {
        return buildPrizeStructureFromLinkedAwards(cat, linked);
    }

    return fromStructure;
}

export function submissionCategoryToWizard(
    cat: SubmissionCategory,
    awardCategories: IAwardCategory[] | undefined,
    awardById?: Map<string, IAwardCategory>,
): ICampaignCategory {
    const lookup = awardById ?? buildAwardLookup(awardCategories);
    return {
        category_name: cat.category_name,
        category_description: plainTextFromHtml(cat.category_description || ''),
        category_awards: [],
        with_fee: cat.with_fee !== false,
        with_tiered_pricing: cat.with_tiered_pricing !== false,
        currency: cat.currency || 'USD',
        fee:
            cat.bare_amount != null
                ? cat.bare_amount / 100
                : cat.fee != null
                ? parseFloat(String(cat.fee)) || 0
                : undefined,
        tier_fee: tierFeeFromSubmission(cat.tier_fee),
        prize_structure: prizeStructureFromSubmission(cat, awardCategories, lookup),
    };
}

/**
 * Map a category from the details drawer (display-sorted list) to its index in contest.categories.
 * Edit/save always use contest.categories order — not display order.
 */
export function resolveContestCategoryIndex(
    contestCategories: SubmissionCategory[] | undefined,
    category: SubmissionCategory,
): number {
    const list = contestCategories ?? [];
    if (category._id) {
        const byId = list.findIndex(c => c._id === category._id);
        if (byId >= 0) return byId;
    }
    const name = category.category_name?.trim();
    if (name) {
        const byName = list.map((c, i) => ({ i, name: c.category_name?.trim() })).filter(x => x.name === name);
        if (byName.length === 1) return byName[0].i;
    }
    return 0;
}

export function buildAwardLookup(awards: IAwardCategory[] | undefined): Map<string, IAwardCategory> {
    const map = new Map<string, IAwardCategory>();
    (awards || []).forEach(a => {
        if (a?._id) map.set(a._id, a);
    });
    return map;
}

export function editModeWizardState(
    contestCategories: SubmissionCategory[] | undefined,
    awardCategories: IAwardCategory[] | undefined,
    editingIndex: number,
): ContestWizardState {
    const awardById = buildAwardLookup(awardCategories);
    const cat = contestCategories?.[editingIndex];
    const categories = cat
        ? [reconcilePrizeStructureWithTiers(submissionCategoryToWizard(cat, awardCategories, awardById))]
        : [emptyWizardCategory()];
    return {
        submission_state: 'public_submission',
        submission_state_message: '',
        title: '',
        description: '',
        feature_image_preview: null,
        feature_image_file: null,
        mobile_feature_image_preview: null,
        mobile_feature_image_file: null,
        requirements: [],
        tag: [],
        genre: [],
        categories_enabled: true,
        categories,
        late_entry: false,
        preselection_enabled: false,
        entry_type: 'multiple',
        max_participants: null,
        hide_submissions_count: false,
        contest_phases_enabled: false,
        contest_phase_dates: [],
        voting_required: false,
        voting_type: 'daily',
        rating: 'PG-13',
        contest_reference: [],
        playlist: [],
        technical_spec: {
            max_duration: 2,
            min_duration: 1,
            project_format: 'video',
            media_type: 'video',
        },
        score_criteria: [],
        contest_media_files: [],
        editingCategoryIndex: 0,
    };
}

export function contestCategoriesToWizardState(
    contestCategories: SubmissionCategory[] | undefined,
    awardCategories: IAwardCategory[] | undefined,
    editingIndex: number,
): ContestWizardState {
    const awardById = buildAwardLookup(awardCategories);
    const categories = (contestCategories || []).map(c =>
        reconcilePrizeStructureWithTiers(submissionCategoryToWizard(c, awardCategories, awardById)),
    );
    return {
        submission_state: 'public_submission',
        submission_state_message: '',
        title: '',
        description: '',
        feature_image_preview: null,
        feature_image_file: null,
        mobile_feature_image_preview: null,
        mobile_feature_image_file: null,
        requirements: [],
        tag: [],
        genre: [],
        categories_enabled: true,
        categories: categories.length ? categories : [emptyWizardCategory()],
        late_entry: false,
        preselection_enabled: false,
        entry_type: 'multiple',
        max_participants: null,
        hide_submissions_count: false,
        contest_phases_enabled: false,
        contest_phase_dates: [],
        voting_required: false,
        voting_type: 'daily',
        rating: 'PG-13',
        contest_reference: [],
        playlist: [],
        technical_spec: {
            max_duration: 2,
            min_duration: 1,
            project_format: 'video',
            media_type: 'video',
        },
        score_criteria: [],
        contest_media_files: [],
        editingCategoryIndex: editingIndex,
    };
}

export function createModeWizardState(): ContestWizardState {
    return contestCategoriesToWizardState([], [], 0);
}

export function appendModeWizardState(
    existing: SubmissionCategory[] | undefined,
    awardCategories: IAwardCategory[] | undefined,
): ContestWizardState {
    const awardById = buildAwardLookup(awardCategories);
    const mapped = (existing || []).map(c =>
        reconcilePrizeStructureWithTiers(submissionCategoryToWizard(c, awardCategories, awardById)),
    );
    return {
        ...contestCategoriesToWizardState([], [], mapped.length),
        categories: [...mapped, emptyWizardCategory()],
        editingCategoryIndex: mapped.length,
    };
}

export function mergeCategoriesForSave(
    mode: 'create' | 'edit',
    existing: SubmissionCategory[] | undefined,
    wizardCategories: ICampaignCategory[],
    editingIndex: number,
): Record<string, unknown>[] {
    const mapped = wizardCategories.map(c => ({
        ...c,
        with_tiered_pricing: c.with_tiered_pricing !== false,
        with_fee: c.with_fee !== false,
    }));
    const prior = existing || [];

    if (mode === 'create') {
        const newCategories = mapped.filter((_, i) => isCategoryFilled(wizardCategories[i]));
        return [
            ...prior.map(c => ({
                category_name: c.category_name,
                category_description: c.category_description,
                with_fee: c.with_fee,
                with_tiered_pricing: c.with_tiered_pricing,
                currency: c.currency,
                tier_fee: c.tier_fee,
                prize_structure: c.prize_structure,
                _id: c._id,
            })),
            ...newCategories,
        ];
    }

    const updated = mapped[0] ?? mapped[editingIndex];
    return prior.map((c, i) => {
        if (i !== editingIndex) {
            return {
                category_name: c.category_name,
                category_description: c.category_description,
                with_fee: c.with_fee,
                with_tiered_pricing: c.with_tiered_pricing,
                currency: c.currency,
                tier_fee: c.tier_fee,
                prize_structure: c.prize_structure,
                _id: c._id,
            };
        }
        return {
            ...updated,
            _id: c._id,
        };
    });
}

function categoryDescriptionText(c: ICampaignCategory): string {
    return plainTextFromHtml(c.category_description || '');
}

export function isCategoryEmpty(c: ICampaignCategory): boolean {
    return !c.category_name?.trim() && !categoryDescriptionText(c);
}

export function isCategoryFilled(c: ICampaignCategory): boolean {
    return Boolean(c.category_name?.trim() && categoryDescriptionText(c));
}

export function isCategoryPartial(c: ICampaignCategory): boolean {
    return !isCategoryEmpty(c) && !isCategoryFilled(c);
}

/** Drop blank slots so awards step only sees categories that can have prizes. */
export function pruneEmptyCategories(categories: ICampaignCategory[]): ICampaignCategory[] {
    return categories.filter(c => !isCategoryEmpty(c));
}

export function canProceedFromCategoryBuilder(categories: ICampaignCategory[]): boolean {
    if (!categories.length) return false;
    if (categories.some(isCategoryPartial)) return false;
    return categories.some(isCategoryFilled) && categories.every(c => isCategoryFilled(c) || isCategoryEmpty(c));
}

export function isCategoryBuilderValid(categories: ICampaignCategory[], idx: number): boolean {
    const c = categories[idx];
    if (!c) return false;
    return isCategoryFilled(c);
}

/** Initial state when creating one or more new categories in the manage modal. */
export function createSessionWizardState(): ContestWizardState {
    return {
        submission_state: 'public_submission',
        submission_state_message: '',
        title: '',
        description: '',
        feature_image_preview: null,
        feature_image_file: null,
        mobile_feature_image_preview: null,
        mobile_feature_image_file: null,
        requirements: [],
        tag: [],
        genre: [],
        categories_enabled: true,
        categories: [emptyWizardCategory()],
        late_entry: false,
        preselection_enabled: false,
        entry_type: 'multiple',
        max_participants: null,
        hide_submissions_count: false,
        contest_phases_enabled: false,
        contest_phase_dates: [],
        voting_required: false,
        voting_type: 'daily',
        rating: 'PG-13',
        contest_reference: [],
        playlist: [],
        technical_spec: {
            max_duration: 2,
            min_duration: 1,
            project_format: 'video',
            media_type: 'video',
        },
        score_criteria: [],
        contest_media_files: [],
        editingCategoryIndex: 0,
    };
}
